import router from './router'
import type { RouteRecordRaw } from 'vue-router'
import { isRelogin } from '@/config/axios/service'
import { getAdminAccessToken, getFirmAccessToken, getMerchantAccessToken, getSupplierAccessToken } from '@/utils/auth'
import { useTitle } from '@/hooks/web/useTitle'
import { useDictStoreWithOut } from '@/store/modules/dict'
import { useUserStoreWithOut } from '@/store/modules/user'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { parseRouteLocation } from '@/utils/routeParams'
import { useWatermark } from '@/hooks/web/useWatermark'
import { getTenantWatermark } from '@/api/system/tenant'
import { storePath, isStorePath, isLegacyMerchantPath } from '@/views/merchant/registry/paths'
import { firmPath, isFirmPath } from '@/views/firm-portal/registry/paths'
import type { AppRouteRecordRaw } from '@/router/types'
import {
  buildAiApplicationCenterRoutes,
  buildAiBusinessCenterRoutes,
  clonePrefixedRoute
} from '@/router/aiApplicationCenter'
import { ensureMallHiddenRoutes } from '@/router/mallHiddenRoutes'
import { ensurePromotionHiddenRoutes } from '@/router/promotionHiddenRoutes'
import { ensureCateringHiddenRoutes } from '@/router/cateringHiddenRoutes'

// 路由不重定向白名单
const whiteList = [
  '/portal',
  '/login',
  storePath('/login'),
  '/social-login',
  '/wecom-entry',
  '/auth-redirect',
  '/bind',
  '/register',
  '/oauthLogin/gitee'
]

// 独立展示窗路由:这些路由在新窗口中打开,复用 access-token 自动登录
// 不需要走菜单路由生成流程,直接放行
const standaloneViewPaths = [
  '/dashboard-view',
  '/ceo-cockpit-view',
  '/mall/cockpit-view',
  '/activity/cockpit-view'
]
const isStandaloneViewPath = (path: string) =>
  standaloneViewPaths.includes(path) ||
  path.startsWith('/diy/template/editor/') ||
  // 商城等 AI 应用中心独立窗会把 /diy 改写成带前缀路径
  path.startsWith('/ai-Application-center/diy/template/editor/')

// 商家端无需登录即可访问的路由
const merchantPublicPaths = [storePath('/login'), storePath('/o2o/institution/apply'), '/b/login']

function isMerchantPublicPath(path: string): boolean {
  return merchantPublicPaths.includes(path)
}

function isSupplierPath(path: string): boolean {
  return path === '/supplier' || path.startsWith('/supplier/')
}

// 路由加载前
router.beforeEach(async (to, from) => {
  // 独立展示窗路由:这些路由在 window.open 打开的新窗口中访问
  // 需要未登录时跳转到登录页,登录后直接放行
  if (isStandaloneViewPath(to.path)) {
    if (!getAdminAccessToken()) {
      return `/login?redirect=${encodeURIComponent(to.fullPath)}`
    }
    // 放行,让组件挂载并发起数据请求
    return true
  }

  // 门户是公开内容，不能被管理端登录态、用户信息、菜单或字典请求阻塞。
  if (isPortalPath(to.path)) {
    clearTenantWatermark()
    return true
  }

  // 登录页同时承担已登录用户的账号入口，不执行后台路由初始化和自动跳转。
  if (to.path === '/login') {
    clearTenantWatermark()
    return true
  }

  // 供应商端：独立会话标记，不走管理员菜单初始化
  if (isSupplierPath(to.path)) {
    clearTenantWatermark()
    if (to.path === '/supplier/login') {
      return true
    }
    const isSupplierSession = localStorage.getItem('supplierUserType') === 'supplier'
    if (getSupplierAccessToken() && isSupplierSession) {
      return true
    }
    return `/supplier/login?redirect=${encodeURIComponent(to.fullPath)}`
  }

  // 律所机构端：独立会话（对标 /store）
  if (isFirmPath(to.path)) {
    clearTenantWatermark()
    const isFirmSession = localStorage.getItem('firmUserType') === 'firm'
    if (to.path === firmPath('/login')) {
      if (getFirmAccessToken() && isFirmSession) {
        return { path: firmPath('/workbench') }
      }
      return true
    }
    if (getFirmAccessToken() && isFirmSession) {
      return true
    }
    return `${firmPath('/login')}?redirect=${encodeURIComponent(to.fullPath)}`
  }

  // 渠道代理商门户：短信登录 + Agent-Token（独立于管理员）
  if (to.path === '/agent-apply') {
    return true
  }
  if (to.path === '/agent-portal' || to.path.startsWith('/agent-portal/')) {
    clearTenantWatermark()
    const hasToken = !!localStorage.getItem('agent_portal_token')
    if (to.path === '/agent-portal/login') {
      if (hasToken) {
        return { path: '/agent-portal/workbench' }
      }
      return true
    }
    if (hasToken) {
      return true
    }
    return `/agent-portal/login?redirect=${encodeURIComponent(to.fullPath)}`
  }

  // 商家路由：独立认证体系，不依赖管理员 token 和权限信息
  if (isStorePath(to.path) || isLegacyMerchantPath(to.path) || to.path.startsWith('/b/')) {
    if (isMerchantPublicPath(to.path)) {
      return true
    }
    const isMerchantSession = localStorage.getItem('merchantUserType') === 'merchant'
    if (getMerchantAccessToken() && isMerchantSession) {
      if (to.path === storePath('/login') || to.path === '/b/login') {
        return { path: storePath('/workbench') }
      }
      return true
    }
    return `${storePath('/login')}?redirect=${encodeURIComponent(to.fullPath)}`
  }
  if (getAdminAccessToken()) {
    const dictStore = useDictStoreWithOut()
    const userStore = useUserStoreWithOut()
    const permissionStore = usePermissionStoreWithOut()
    // 异步加载字典
    // 另外，间接 issue：https://gitee.com/yudaocode/yudao-ui-admin-vue3/issues/ID9FLI
    if (!dictStore.getIsSetDict) {
      void dictStore.setDictMap().catch(() => undefined)
    }
    // 修复：页面刷新后 isSetUser 可能已被 Pinia 持久化恢复为 true，
    // 但 permissionStore.routers 未持久化（persist: false），
    // 导致 generateRoutes 被跳过、左侧菜单为空。
    // 增加 routers 为空时的兜底判断。
    if (!userStore.getIsSetUser || permissionStore.getRouters.length === 0) {
      isRelogin.show = true
      await userStore.setUserInfoAction()
      isRelogin.show = false
      // 过期 token 已被清掉：跳登录，避免 generateRoutes/isReady 卡死导致独立窗白屏
      if (!getAdminAccessToken() || !userStore.getIsSetUser) {
        return `/login?redirect=${encodeURIComponent(to.fullPath)}`
      }
      // 后端过滤菜单
      await permissionStore.generateRoutes()
      // 仅移除「纯跳转/通配吞路由」的旧桩。勿删 CateringStandaloneAiAppPrefixed：
      // 该桩已挂真实 dashboard/cockpit 叶子；删掉后若后端菜单未注入或租户无餐饮菜单，
      // /ai-Application-center/catering/dashboard 会无匹配组件 → 独立窗整页白屏。
      for (const staleName of [
        'CateringCatchAllRedirect',
        'RepastStandaloneAiAppPrefixed',
        'RepastCatchAllRedirect'
      ]) {
        if (router.hasRoute(staleName)) {
          router.removeRoute(staleName)
        }
      }
      permissionStore.getAddRouters.forEach((route) => {
        // 单条非法路由（如顶级 path 缺 /）不得拖垮整表注册，否则独立窗永久白屏
        try {
        // 路由同名冲突处理：动态模块路由不能覆盖后端菜单路由
        // 现象：ai.ts 的 /ai 与后端菜单的 /ai 同名，后者覆盖前者导致 /ai/chat 404
        // 修复：同名路由只追加 children，不替换
        const name = (route as any).name as string | undefined
        if (name && router.hasRoute(name)) {
          const children = (route as any).children as any[] | undefined
          if (children?.length) {
            // AI 应用中心独立窗：同名合并时（如后端 /ai 与 ai.ts 的 Ai），用后端菜单重建前缀父路由，
            // 替换动态模块副本（裸 RouterView）。保证：
            // 1) 独立窗外壳（Layout）正常渲染；
            // 2) 后端子菜单（写作模型等）的前缀 URL 可命中，不再 404。
            // 仅对后端菜单路由重建（后端目录路由必有 redirect，动态模块路由如 ai.ts 没有），
            // 避免后续处理 ai.ts 时用其子路由覆盖掉后端菜单的前缀副本。
            const prefixedParentName = `${name}AiAppCenter`
            const isBackendMenuRoute = typeof (route as any).redirect === 'string'
            if (isBackendMenuRoute) {
              for (const prefixed of buildAiApplicationCenterRoutes([route as AppRouteRecordRaw])) {
                try {
                  router.addRoute(prefixed)
                } catch (e) {
                  console.error('[permission] addRoute prefixed failed', prefixed.path, e)
                }
              }
            }
            children.forEach((child: any) => {
              // 裸路径父路由：缺则补
              if (child?.name && !router.hasRoute(child.name)) {
                try {
                  router.addRoute(name, child)
                } catch (e) {
                  console.error('[permission] addRoute child failed', child?.path, e)
                }
              }
              // AI 应用中心独立窗：前缀父路由被后端菜单重建后，会冲掉 ai.ts 里已注册的隐藏子路由
              // （如 knowledge/document、retrieval、segment）。此时裸名 AiKnowledgeDocument 仍在，
              // 但 AiKnowledgeDocumentAiAppCenter 已没了；若仍用「裸名不存在才同步前缀」会漏补，
              // 独立窗点「文档」→ 先命中裸路径再被改写成 /ai-Application-center/... → 404。
              if (router.hasRoute(prefixedParentName)) {
                const prefixedChild = clonePrefixedRoute(child as AppRouteRecordRaw)
                if (prefixedChild.name && !router.hasRoute(prefixedChild.name)) {
                  try {
                    router.addRoute(prefixedParentName, prefixedChild)
                  } catch (e) {
                    console.error('[permission] addRoute prefixed child failed', prefixedChild.path, e)
                  }
                }
              }
            })
          }
        } else {
          try {
            router.addRoute(route as unknown as RouteRecordRaw)
          } catch (e) {
            console.error('[permission] addRoute failed', (route as any)?.path, e)
          }
          // AI 应用中心独立窗：后端菜单中的 AI 模块顶层路由（如 /fin）同步注册前缀副本，
          // 保证 /ai-Application-center/xxx 前缀 URL 可直接命中路由
          for (const prefixed of buildAiApplicationCenterRoutes([route as AppRouteRecordRaw])) {
            try {
              router.addRoute(prefixed)
            } catch (e) {
              console.error('[permission] addRoute center prefixed failed', prefixed.path, e)
            }
          }
          for (const prefixed of buildAiBusinessCenterRoutes([route as AppRouteRecordRaw])) {
            try {
              router.addRoute(prefixed)
            } catch (e) {
              console.error('[permission] addRoute biz prefixed failed', prefixed.path, e)
            }
          }
        }
        } catch (e) {
          console.error(`[permission] 跳过非法路由注册: ${(route as any)?.path}`, e)
        }
      })
      // 后台 /mall 菜单树就绪后：商品/订单/入驻等隐藏页挂到同一 Layout（独立窗留菜单）
      ensureMallHiddenRoutes(router)
      // 后台 /ai-Application-center/promotion 菜单树就绪后：执行明细/中奖记录/审批工作台挂到同一 Layout
      ensurePromotionHiddenRoutes(router)
      // 餐饮总裁驾驶舱快捷入口：积分管理 / 文档帮助 等隐藏页挂到餐饮 Layout
      ensureCateringHiddenRoutes(router)
      // 如果只是补生成路由（非首次登录），需要 re-navigate 触发路由重新匹配
      // 直接放行会保留旧的 404 匹配，导致动态模块页刷新后 404
      if (userStore.getIsSetUser && permissionStore.getRouters.length > 0) {
        // 重新导航以便匹配刚生成的动态路由。
        void applyTenantWatermark(to.path)
        return { ...to, replace: true }
      }
      const redirectPath = from.query.redirect
      // 修复跳转时不带参数的问题
      const redirect = typeof redirectPath === 'string' ? redirectPath : to.fullPath
      const redirectLocation = parseRouteLocation(redirect)
      const nextData =
        to.fullPath === redirect ? { ...to, replace: true } : { ...redirectLocation, replace: true }
      void applyTenantWatermark(to.path)
      return nextData
    }
    // 仅管理后台业务页面应用租户水印
    void applyTenantWatermark(to.path)
    return true
  } else {
    // 门户页面 /portal 及其子路由（/portal/home、/portal/products 等）均放行
    if (whiteList.indexOf(to.path) !== -1 || to.path.startsWith('/portal/')) {
      return true
    }
    // 独立窗 / 中心前缀深链：未登录直达登录页并带回跳，避免落到门户首页后丢 redirect
    const isStandaloneDeepLink =
      to.query.standalone === '1' ||
      to.path.startsWith('/ai-Application-center') ||
      to.path.startsWith('/ai-business-center')
    if (isStandaloneDeepLink) {
      return `/login?redirect=${encodeURIComponent(to.fullPath)}`
    }
    return `/portal?redirect=${encodeURIComponent(to.fullPath)}` // 否则全部重定向到门户
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
})

/** 门户路由不属于租户后台，不应用页面水印 */
const isPortalPath = (path: string) => path === '/portal' || path.startsWith('/portal/')

let watermarkController: ReturnType<typeof useWatermark> | undefined
const getWatermarkController = () => (watermarkController ??= useWatermark())

function clearTenantWatermark() {
  getWatermarkController().clear()
}

/** 应用当前租户的页面水印 */
let tenantWatermarkPromise: ReturnType<typeof getTenantWatermark> | undefined
async function applyTenantWatermark(path: string) {
  if (isPortalPath(path)) {
    clearTenantWatermark()
    return
  }
  try {
    // 每个会话只请求一次配置；从门户返回后台时可直接恢复水印。
    tenantWatermarkPromise ??= getTenantWatermark()
    const wm = await tenantWatermarkPromise
    // 请求期间可能已经导航到门户，避免异步响应把水印重新覆盖上去。
    if (isPortalPath(router.currentRoute.value.path)) {
      clearTenantWatermark()
      return
    }
    const { setWatermark, clear } = getWatermarkController()
    if (wm?.enabled && wm?.text) {
      setWatermark(wm.text, {
        fontSize: wm.fontSize,
        color: wm.color,
        gap: wm.gap,
        opacity: wm.opacity
      })
    } else {
      clear()
    }
  } catch {
    // 忽略错误，不影响正常使用
  }
}
